import { createSignal } from 'solid-js'
import { createStore, produce } from 'solid-js/store'

// ---------------------------------------------------------------------------
// Live car-mirror / gateway transfer progress store.
//
// Standalone module (no imports from sync-service / retrievePubToThread) so it
// can be read by the UI and written by the fetch/push paths without a cycle.
//
// Keyed by the transfer target id — for pulls that's the ipns/root id passed to
// retrievePubToThread (which equals a subscription's id and the preview's pubID),
// for pushes it's the share id.
// ---------------------------------------------------------------------------

export type TransferKind = 'pull' | 'push'
export type TransferSource = 'car-mirror' | 'gateway'
export type TransferPhase = 'resolving' | 'transferring' | 'decoding' | 'done' | 'error'

export interface TransferProgress {
	id: string
	kind: TransferKind
	source: TransferSource
	phase: TransferPhase
	/** human label (sub/share name) if known */
	label?: string
	/** 1-based round number (car-mirror) */
	round?: number
	maxRounds?: number
	/** cumulative blocks transferred */
	blocks: number
	/** blocks transferred in the most recent round */
	blocksThisRound?: number
	/** cumulative bytes transferred over the wire */
	bytes: number
	/** bytes in the most recent round */
	bytesThisRound?: number
	/** remaining frontier: wanted blocks (pull) / subgraph roots (push) */
	frontier?: number
	/** bloom filter size (bytes) for the current round */
	bloomBytes?: number
	/** bloom hash count k */
	bloomK?: number
	/** HTTP round-trips issued so far */
	requests?: number
	/** snapshots walked (gateway incremental fetch) */
	snapshots?: number
	/** push pipeline step currently running (blocks → remotes → kubo → ipns) */
	step?: string
	error?: string
	startedAt: number
	updatedAt: number
}

const [store, setStore] = createStore<Record<string, TransferProgress>>({})
/** Reactive map of id → progress. Use with `transferIds()` to iterate. */
export const transferProgress = store

// keys aren't reactively iterable on a solid store, so track them in a signal
const [ids, setIds] = createSignal<string[]>([])
/** Reactive list of active transfer ids (for iterating the store). */
export const transferIds = ids

/**
 * Merge a progress patch for `id`. A new transfer (phase 'resolving') resets the
 * entry so stale stats from a prior run don't linger; every other phase merges in.
 *
 * Entries are NOT auto-cleared: the final 'done'/'error' stats persist until the
 * next transfer for this id (or an explicit clearTransferProgress), so they don't
 * vanish while they're still being read. The title-bar tooltip only renders
 * entries for currently-active syncs, so persistence doesn't clutter it.
 */
export function reportTransferProgress(
	id: string,
	patch: Partial<TransferProgress> & { kind: TransferKind },
) {
	const now = Date.now()
	// strip undefined so a partial round event never blanks out an earlier field
	const clean: Record<string, unknown> = {}
	for (const k in patch) {
		const v = (patch as Record<string, unknown>)[k]
		if (v !== undefined) clean[k] = v
	}
	// A fresh transfer (resolving) discards any lingering stats from a previous run.
	if (patch.phase === 'resolving' && store[id]) {
		setStore(produce((s) => {
			delete s[id]
		}))
	}
	if (!store[id]) {
		setStore(id, {
			id,
			source: 'car-mirror',
			phase: 'transferring',
			blocks: 0,
			bytes: 0,
			startedAt: now,
			updatedAt: now,
			...clean,
		} as TransferProgress)
		setIds(prev => (prev.includes(id) ? prev : [...prev, id]))
	} else {
		setStore(id, { ...clean, updatedAt: now })
	}
}

export function clearTransferProgress(id: string) {
	setStore(produce((s) => {
		delete s[id]
	}))
	setIds(prev => prev.filter(x => x !== id))
}

/** Bytes/second since the transfer started (cumulative average). */
export function transferRate(p: TransferProgress): number {
	const secs = Math.max(0.001, (p.updatedAt - p.startedAt) / 1000)
	return p.bytes / secs
}

const UNITS = ['B', 'KB', 'MB', 'GB']
export function formatBytes(n: number | undefined): string {
	if (!n || n < 1) return '0 B'
	const i = Math.min(UNITS.length - 1, Math.floor(Math.log(n) / Math.log(1024)))
	const val = n / 1024 ** i
	return `${val >= 100 || i === 0 ? Math.round(val) : val.toFixed(1)} ${UNITS[i]}`
}
